import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import styles from './statistics.module.scss';
import { animate, motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const variants = {
  initial: { opacity: 0 },
  visible: i => ({
    opacity: 1,
    transition: {
      duration: 1.3,
      delay: i * 0.3,
    },
  }),
  hidden: { opacity: 0 },
};

const circleConfig = {
  viewBox: '0 0 33 33',
  x: '16.5',
  y: '16.5',
  radio: '16',
};

const Statistics = ({ data }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: '-100px 0px',
  });

  if (data.listing && Array.isArray(data.listing)) {
    return (
      <div ref={ref} className={styles.wrapper}>
        {data.listing.map((stat, k) => (
          <Stat key={stat.id} data={stat} k={k} inView={inView} />
        ))}
      </div>
    );
  }

  return null;
};

const Stat = ({ data, k, inView, from = 0 }) => {
  const [to, setTo] = useState(0);
  const [progressBar, setProgressBar] = useState(0);
  const [percentage, setPercentage] = useState(0);
  const nodeRef = useRef();

  const { locale } = useRouter();

  const updatePercentage = () => {
    setTimeout(() => {
      setProgressBar(progressBar + 1);
    }, 15);
  };

  useEffect(() => {
    if (percentage > 0) updatePercentage();
  }, [percentage]);

  useEffect(() => {
    if (progressBar < percentage) updatePercentage();
  }, [progressBar]);

  useEffect(() => {
    if (inView) {
      setTo(Number(data.title));
      const timeout = setTimeout(() => {
        setPercentage(75);
      }, k * 500);
    }
  }, [inView]);

  useEffect(() => {
    const node = nodeRef.current;

    const controls = animate(from, to, {
      duration: 2,
      onUpdate(value) {
        node.textContent = value.toFixed(0).toLocaleString(locale);
      },
    });

    return () => {
      controls.stop();
    };
  }, [to]);

  return (
    <motion.div
      custom={k}
      initial="initial"
      animate={inView ? 'visible' : 'false'}
      exit="hidden"
      variants={variants}
      className={styles.indicatorWrapper}
    >
      <svg className={styles.percentage} viewBox={circleConfig.viewBox}>
        <circle
          className="ring"
          cx={circleConfig.x}
          cy={circleConfig.y}
          r={circleConfig.radio}
          fill="transparent"
          stroke="#546067"
          strokeWidth="0.7"
        />
        <circle
          className="path"
          cx={circleConfig.x}
          cy={circleConfig.y}
          r={circleConfig.radio}
          fill="transparent"
          stroke="#ffffff"
          strokeLinecap="round"
          strokeDasharray={`${progressBar} ${100 - progressBar}`}
          strokeDashoffset="25"
        />
      </svg>
      <div className={styles.indicator}>
        <div ref={nodeRef} className={styles.indicatorTitle}>
          {data.title}
        </div>
        <div className={styles.indicatorDesc}>{data.description}</div>
      </div>
    </motion.div>
  );
};

export default Statistics;
